import { useState, useEffect, useCallback } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import {
  Phone, Mail, Calendar, GraduationCap, PhoneCall, UserCheck,
  StickyNote, Clock, CheckCircle, CheckCircle2, XCircle, AlertTriangle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import StudentProfilePanel from '@/components/training/StudentProfilePanel';
import CustomerJourney from './CustomerJourney';

const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Contacted', color: 'bg-cyan-100 text-cyan-700' },
  qualified: { label: 'Qualified', color: 'bg-indigo-100 text-indigo-700' },
  registration_booked: { label: 'Registration Booked', color: 'bg-green-100 text-green-700' },
  registered: { label: 'Registered', color: 'bg-emerald-100 text-emerald-700' },
  enrolled: { label: 'Enrolled', color: 'bg-pink-100 text-pink-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800' },
  lost: { label: 'Lost', color: 'bg-slate-100 text-slate-500' },
  booked: { label: 'Booked', color: 'bg-green-100 text-green-700' },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700' },
};

const CALLBACK_QUICK_OPTIONS = [
  { label: 'Tomorrow 10am', days: 1, hour: 10 },
  { label: 'Tomorrow 2pm', days: 1, hour: 14 },
  { label: 'In 3 days', days: 3, hour: 10 },
  { label: 'Next week', days: 7, hour: 10 },
];

const DepositControl = ({ lead, onChanged }) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (paid) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/training/leads/${lead.lead_id}/deposit`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paid, amount: amount ? parseFloat(amount) : undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(paid ? `Deposit recorded${body.referral_invite_sent ? ' · refer-a-friend message sent' : ''}` : 'Deposit removed');
        setAmount('');
        onChanged();
      } else {
        toast.error(body.detail || 'Could not save');
      }
    } finally {
      setSaving(false);
    }
  };

  if (lead.deposit_paid) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
        <span className="text-sm text-green-800">
          Deposit paid{lead.deposit_amount ? ` · £${Number(lead.deposit_amount).toLocaleString('en-GB')}` : ''}
          {lead.deposit_paid_at ? ` · ${new Date(lead.deposit_paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
        </span>
        <button type="button" className="text-xs text-green-700 underline" disabled={saving} onClick={() => save(false)}>Undo</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
      <span className="text-sm text-slate-700 whitespace-nowrap">Deposit £</span>
      <input
        type="number" min="0" step="0.01" inputMode="decimal" value={amount} placeholder="amount"
        onChange={(e) => setAmount(e.target.value)} aria-label="Deposit amount"
        className="w-24 h-9 rounded-lg border border-slate-200 px-2 text-sm"
      />
      <Button size="sm" className="ml-auto rounded-lg bg-green-600 hover:bg-green-700 text-white" disabled={saving} onClick={() => save(true)}>
        Mark deposit paid
      </Button>
    </div>
  );
};

const LeadDetailDrawer = ({ open, onOpenChange, leadId, onLeadUpdated }) => {
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);

  const fetchJourney = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/training/leads/${leadId}/journey`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        setJourney(await res.json());
      } else {
        toast.error('Failed to load student details');
      }
    } catch (err) {
      console.error('Journey fetch error:', err);
      toast.error('Failed to load student details');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open && leadId) {
      fetchJourney();
    }
    if (!open) {
      setJourney(null);
      setNote('');
    }
  }, [open, leadId, fetchJourney]);

  const handleAddNote = async () => {
    if (!note.trim() || !leadId) return;
    setSavingNote(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/leads/${leadId}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ content: note.trim() }),
      });
      if (res.ok) {
        toast.success('Note added');
        setNote('');
        fetchJourney();
      }
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleScheduleCallback = async (option) => {
    try {
      const token = localStorage.getItem('token');
      const callbackDate = new Date();
      callbackDate.setDate(callbackDate.getDate() + option.days);
      callbackDate.setHours(option.hour, 0, 0, 0);

      const res = await fetch(`${API_URL}/api/callbacks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          lead_id: leadId,
          scheduled_for: callbackDate.toISOString(),
          reason: 'Follow-up call',
          priority: 'medium',
        }),
      });
      if (res.ok) {
        toast.success(`Callback scheduled for ${option.label}`);
        setCallbackOpen(false);
        fetchJourney();
        if (onLeadUpdated) onLeadUpdated();
      }
    } catch (err) {
      toast.error('Failed to schedule callback');
    }
  };

  const handleMarkRegistered = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ pipeline_stage: 'registered' }),
      });
      if (res.ok) {
        toast.success('Marked as registered');
        fetchJourney();
        if (onLeadUpdated) onLeadUpdated();
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleMarkQualified = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/leads/${leadId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipeline_stage: 'qualified' }),
      });
      if (res.ok) {
        toast.success('Marked as qualified');
        fetchJourney();
        if (onLeadUpdated) onLeadUpdated();
      } else {
        toast.error('Failed to update');
      }
    } catch {
      toast.error('Failed to update');
    }
  };

  const lead = journey?.lead;
  const timeline = journey?.timeline || [];
  const enrollment = journey?.enrollment;
  const attendanceStats = journey?.attendance_stats;

  const stageCfg = STAGE_CONFIG[lead?.pipeline_stage] || STAGE_CONFIG.new;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-pink-400 animate-spin" />
          </div>
        ) : !lead ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-slate-400">Student not found</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b z-10 px-5 py-4">
              <SheetHeader className="mb-3">
                <SheetTitle className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {lead.name || [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Student'}
                </SheetTitle>
              </SheetHeader>

              {/* Contact actions */}
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {lead.phone && (
                  <a
                    href={`tel:${lead.phone}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-pink-50 text-pink-600 text-sm font-medium hover:bg-pink-100 transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    {lead.phone}
                  </a>
                )}
                {lead.email && (
                  <a
                    href={`mailto:${lead.email}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 text-blue-600 text-sm hover:bg-blue-100 transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span className="max-w-[160px] truncate">{lead.email}</span>
                  </a>
                )}
              </div>

              {/* Status badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-xs ${stageCfg.color}`}>{stageCfg.label}</Badge>
                {lead.manual_followup_required && (
                  <Badge className="text-xs bg-amber-100 text-amber-700">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Needs Follow-up
                  </Badge>
                )}
                {lead.course_interest && (
                  <Badge className="text-xs bg-pink-50 text-pink-600">
                    <GraduationCap className="w-3 h-3 mr-1" />
                    {lead.course_interest}
                  </Badge>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <Tabs defaultValue="profile" className="w-full">
                <TabsList className="w-full grid grid-cols-4 mb-4">
                  <TabsTrigger value="profile" className="text-xs">Profile</TabsTrigger>
                  <TabsTrigger value="journey" className="text-xs">Journey</TabsTrigger>
                  <TabsTrigger value="actions" className="text-xs">Actions</TabsTrigger>
                  <TabsTrigger value="notes" className="text-xs">Notes</TabsTrigger>
                </TabsList>

                <TabsContent value="profile">
                  <StudentProfilePanel leadId={leadId} refreshKey={journey} />
                </TabsContent>

                {/* Journey Tab */}
                <TabsContent value="journey">
                  {/* Attendance stats if enrolled */}
                  {attendanceStats && (
                    <div className="mb-4 p-3 bg-green-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <GraduationCap className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          {enrollment?.course_name || 'Enrolled'}
                        </span>
                      </div>
                      <p className="text-xs text-green-600">
                        Attendance: {attendanceStats.attended}/{attendanceStats.total} classes
                        ({attendanceStats.percentage}%)
                      </p>
                    </div>
                  )}

                  <CustomerJourney
                    timeline={timeline}
                    onScheduleCallback={() => setCallbackOpen(true)}
                  />
                </TabsContent>

                {/* Actions Tab */}
                <TabsContent value="actions">
                  <div className="space-y-3">
                    {/* Schedule Callback */}
                    <Popover open={callbackOpen} onOpenChange={setCallbackOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start rounded-xl h-12 border-pink-200 text-pink-600 hover:bg-pink-50"
                        >
                          <PhoneCall className="w-4 h-4 mr-3" />
                          Schedule Callback
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-2" align="start">
                        <div className="space-y-1">
                          {CALLBACK_QUICK_OPTIONS.map((opt) => (
                            <button
                              key={opt.label}
                              className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-pink-50 text-slate-700 transition-colors"
                              onClick={() => handleScheduleCallback(opt)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Deposit */}
                    <DepositControl lead={lead} onChanged={() => { fetchJourney(); onLeadUpdated?.(); }} />

                    {/* Mark Qualified */}
                    {!lead.qualified_at && ['new', undefined, null, ''].includes(lead.pipeline_stage) && (
                      <Button
                        variant="outline"
                        className="w-full justify-start rounded-xl h-12 border-violet-200 text-violet-700 hover:bg-violet-50"
                        onClick={handleMarkQualified}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-3" />
                        Mark as Qualified
                      </Button>
                    )}

                    {/* Mark Registered */}
                    {lead.pipeline_stage !== 'registered' && lead.pipeline_stage !== 'enrolled' && lead.pipeline_stage !== 'completed' && (
                      <Button
                        variant="outline"
                        className="w-full justify-start rounded-xl h-12 border-green-200 text-green-600 hover:bg-green-50"
                        onClick={handleMarkRegistered}
                      >
                        <UserCheck className="w-4 h-4 mr-3" />
                        Mark as Registered
                      </Button>
                    )}

                    {/* Call Now */}
                    {lead.phone && (
                      <a
                        href={`tel:${lead.phone}`}
                        className="flex items-center w-full justify-start rounded-xl h-12 border border-blue-200 text-blue-600 hover:bg-blue-50 px-4 transition-colors"
                      >
                        <Phone className="w-4 h-4 mr-3" />
                        Call Now
                      </a>
                    )}
                  </div>
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes">
                  <div className="space-y-3">
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Add a note about this student..."
                      className="rounded-xl resize-none min-h-[100px]"
                    />
                    <Button
                      className="btn-primary rounded-xl w-full"
                      disabled={!note.trim() || savingNote}
                      onClick={handleAddNote}
                    >
                      {savingNote ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <StickyNote className="w-4 h-4 mr-2" />
                      )}
                      Add Note
                    </Button>

                    {/* Existing notes from timeline */}
                    <div className="mt-4 space-y-2">
                      {timeline
                        .filter((e) => e.type === 'note_added' || e.event_type === 'note_added')
                        .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
                        .map((evt, idx) => (
                          <div key={evt.event_id || idx} className="p-3 bg-slate-50 rounded-xl">
                            <p className="text-sm text-slate-700">
                              {evt.event_payload?.content || evt.payload?.content || 'Note'}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(evt.date || evt.created_at).toLocaleDateString('en-GB', {
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                              })}
                              {(evt.created_by_name || evt.event_payload?.created_by_name) &&
                                ` — ${evt.created_by_name || evt.event_payload?.created_by_name}`}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default LeadDetailDrawer;
